import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  IconButton,
} from '@mui/material';
import { GlassButton } from '../../../components/ui';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { useLocaleChange } from '../../../hooks/useLocaleChange';
import { useInfiniteScroll } from '../../../hooks';
import { getErrorMessage } from '../../../api/utils/error-handler';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useWikiTypes } from '../../../contexts/WikiTypesContext';
import WikiItemCard from './WikiItemCard';
import WikiNpcCard from './WikiNpcCard';
import WikiSkillCard from './WikiSkillCard';
import type { WikiNewItem, WikiNpcItem, WikiSkillItem, WikiNewItemFilters } from '../../../types';
import { wikiNewApi } from '../../../api/wiki-new.service';
import { isWikiDetailPage } from '../../../utils/wikiUrlParser';
import { getSourceConfig, SourceConfig } from '../../../config/wikiFiltersConfig';
import { TEXT_COLORS, BG_COLORS, BORDER_COLORS } from '../../../utils/colors';
import { SPACING, INFINITE_SCROLL_PAGE_SIZE, SCROLL_RESERVE } from '../../../utils/responsive';
import { useVirtualizer, Virtualizer, VirtualItem } from '@tanstack/react-virtual';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

// Static filter options
const OPERATOR_TYPES = ['ACTIVE', 'PASSIVE', 'TOGGLE'];
const CRYSTAL_TYPES = ['BA', 'EX', 'EL', 'MA'];
const RANKS = ['LOW', 'MID', 'HIGH', 'TOP', 'REL'];
// Опции фильтра «Уровень» строятся 1..max (max задаётся per-source в wikiFiltersConfig, дефолт 10).
const levelsUpTo = (max: number): number[] => Array.from({ length: max }, (_, i) => i + 1);

interface WikiAppliedFilters {
  search: string;
  sortBy: string;
  rank: string;
  crystalType: string;
  operatorType: string;
  level: string;
  wikiItemLevel: string;
}

const DEFAULT_SORT = 'name';

// ─── Возврат «назад» на то же место (см. docs/wiki.md §3a) ───────────────────
// Уходя со списка, запоминаем, сколько карточек было подгружено и где стояла прокрутка — под ключом
// записи истории. При возврате по истории (POP) догружаем столько же и ставим прокрутку обратно.
interface SavedListPosition {
  navigationKey: string;
  count: number;
  /**
   * Позиция — не пиксели, а «первая карточка верхней видимой строки + сдвиг от начала строки»: сетка
   * виртуализирована, высоты ещё не показанных строк после возврата известны только приблизительно.
   */
  anchorIndex: number;
  anchorOffset: number;
  /**
   * Реальные высоты уже показанных строк `[ключ строки, высота]` и число колонок, при котором они
   * мерились. Без них строки над якорем после возврата считаются по оценке, и якорь съезжал на
   * несколько десятков px. При другом числе колонок (повернули телефон) высоты не годятся — не берём.
   */
  rowSizes?: [string | number, number][];
  columns?: number;
  /** Из какой загрузки приложения сохранено — см. readSavedPosition. */
  loadId: string;
}

const POSITION_STORAGE_PREFIX = 'wiki-list-position:';
// Момент загрузки документа: свой у каждой загрузки/F5 и не меняется при горячей подмене модулей в dev
// (случайное число в модуле пересоздавалось при HMR, и позиция считалась «из прошлой загрузки»).
const PAGE_LOAD_ID = String(performance.timeOrigin);

const readSavedPosition = (historyKey: string): SavedListPosition | null => {
  try {
    const raw = sessionStorage.getItem(POSITION_STORAGE_PREFIX + historyKey);
    const saved: SavedListPosition | null = raw ? JSON.parse(raw) : null;
    if (!saved || typeof saved.anchorIndex !== 'number') return null;
    // У первой записи истории после загрузки страницы ключ всегда 'default', и открытие страницы тоже
    // считается POP. Чтобы заново набранный адрес не прыгал в старую позицию, позицию из прошлой
    // загрузки берём только при F5 и при возврате «назад» с другого сайта.
    if (saved.loadId !== PAGE_LOAD_ID) {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (nav?.type !== 'reload' && nav?.type !== 'back_forward') return null;
    }
    return saved;
  } catch {
    return null;
  }
};

const writeSavedPosition = (historyKey: string, position: SavedListPosition) => {
  try {
    sessionStorage.setItem(POSITION_STORAGE_PREFIX + historyKey, JSON.stringify(position));
  } catch {
    // приватный режим / переполнено — просто не восстановим позицию
  }
};

// ─── Filter Bar (React.memo child — typing doesn't re-render parent) ─────────

const WikiFilterBar: React.FC<{
  isSm: boolean;
  sortBy: string;
  rank: string;
  crystalType: string;
  operatorType: string;
  level: string;
  wikiItemLevel: string;
  initialSearch: string;
  clearTrigger: number;
  hasRankFilter: boolean;
  hasCrystalFilter: boolean;
  hasOperatorTypeFilter: boolean;
  hasLevelFilter: boolean;
  hasWikiItemLevelFilter: boolean;
  wikiItemLevelMax: number;
  hasSorting: boolean;
  hasAnyFilter: boolean;
  sortOptions: { value: string; label: string }[];
  npcLevels: number[];
  npcLevelsLoading: boolean;
  onFiltersChange: (filters: WikiAppliedFilters) => void;
}> = React.memo(({
  isSm,
  sortBy,
  rank,
  crystalType,
  operatorType,
  level,
  wikiItemLevel,
  initialSearch,
  clearTrigger,
  hasRankFilter,
  hasCrystalFilter,
  hasOperatorTypeFilter,
  hasLevelFilter,
  hasWikiItemLevelFilter,
  wikiItemLevelMax,
  hasSorting,
  hasAnyFilter,
  sortOptions,
  npcLevels,
  npcLevelsLoading,
  onFiltersChange,
}) => {
  const { t } = useTranslation('wiki');
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  // Clear local input when parent signals (category change)
  useEffect(() => {
    if (clearTrigger > 0) {
      setSearchInput('');
    }
  }, [clearTrigger]);

  const getCurrentFilters = (overrides: Partial<WikiAppliedFilters> = {}): WikiAppliedFilters => ({
    search: searchInput.trim(),
    sortBy,
    rank,
    crystalType,
    operatorType,
    level,
    wikiItemLevel,
    ...overrides,
  });

  const applyAllFilters = () => {
    onFiltersChange(getCurrentFilters());
  };

  const handleSortChange = (newSort: string) => {
    onFiltersChange(getCurrentFilters({ sortBy: newSort }));
  };

  const handleSelectChange = (key: keyof WikiAppliedFilters, value: string) => {
    onFiltersChange(getCurrentFilters({ [key]: value }));
  };

  const clearAll = () => {
    setSearchInput('');
    setFiltersDrawerOpen(false);
    onFiltersChange({
      search: '',
      sortBy: DEFAULT_SORT,
      rank: '',
      crystalType: '',
      operatorType: '',
      level: '',
      wikiItemLevel: '',
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: { xs: 'nowrap', sm: 'wrap' }, pt: { xs: 0, sm: 1 } }}>
        {/* Filter button - mobile only */}
        {!isSm && (hasAnyFilter || hasSorting) && (
          <Box
            onClick={() => setFiltersDrawerOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'transparent',
              border: `1px solid ${BORDER_COLORS.LIGHT}`,
              borderRadius: '4px',
              px: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: BG_COLORS.GLASS,
                borderColor: BORDER_COLORS.LIGHTER,
              },
            }}
          >
            <TuneIcon sx={{ color: TEXT_COLORS.MUTED, fontSize: '1rem' }} />
          </Box>
        )}

        {/* Search */}
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            applyAllFilters();
          }}
          sx={{ flex: 1 }}
        >
          <TextField
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: TEXT_COLORS.MUTED }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
          />
        </Box>

        {/* Filters - desktop only */}
        {isSm && hasRankFilter && (
          <FormControl size="small" sx={{
            minWidth: 100,
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
              fontSize: '0.875rem',
              transform: 'translate(14px, 7px) scale(1)',
            }
          }}>
            <InputLabel>{t('filters.rank')}</InputLabel>
            <Select
              value={rank}
              label={t('filters.rank')}
              onChange={(e) => handleSelectChange('rank', e.target.value)}
              sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              <MenuItem value="">{t('filters.allRanks')}</MenuItem>
              {RANKS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {isSm && hasCrystalFilter && (
          <FormControl size="small" sx={{
            minWidth: 100,
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
              fontSize: '0.875rem',
              transform: 'translate(14px, 7px) scale(1)',
            }
          }}>
            <InputLabel>{t('filters.crystal')}</InputLabel>
            <Select
              value={crystalType}
              label={t('filters.crystal')}
              onChange={(e) => handleSelectChange('crystalType', e.target.value)}
              sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              <MenuItem value="">{t('filters.allCrystals')}</MenuItem>
              {CRYSTAL_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {isSm && hasOperatorTypeFilter && (
          <FormControl size="small" sx={{
            minWidth: 120,
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
              fontSize: '0.875rem',
              transform: 'translate(14px, 7px) scale(1)',
            }
          }}>
            <InputLabel>{t('filters.operatorType')}</InputLabel>
            <Select
              value={operatorType}
              label={t('filters.operatorType')}
              onChange={(e) => handleSelectChange('operatorType', e.target.value)}
              sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              <MenuItem value="">{t('filters.allOperatorTypes')}</MenuItem>
              {OPERATOR_TYPES.map((op) => <MenuItem key={op} value={op}>{t(`filters.operatorTypes.${op}`)}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {isSm && hasLevelFilter && (
          <FormControl size="small" sx={{
            minWidth: 100,
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
              fontSize: '0.875rem',
              transform: 'translate(14px, 7px) scale(1)',
            }
          }}>
            <InputLabel>{t('filters.level')}</InputLabel>
            <Select
              value={level}
              label={t('filters.level')}
              onChange={(e) => handleSelectChange('level', e.target.value)}
              disabled={npcLevelsLoading || npcLevels.length === 0}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              <MenuItem value="">{t('filters.allLevels')}</MenuItem>
              {npcLevels.map((l) => <MenuItem key={l} value={l.toString()}>{l}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {isSm && hasWikiItemLevelFilter && (
          <FormControl size="small" sx={{
            minWidth: 100,
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
              fontSize: '0.875rem',
              transform: 'translate(14px, 7px) scale(1)',
            }
          }}>
            <InputLabel>{t('filters.level')}</InputLabel>
            <Select
              value={wikiItemLevel}
              label={t('filters.level')}
              onChange={(e) => handleSelectChange('wikiItemLevel', e.target.value)}
              sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              <MenuItem value="">{t('filters.allLevels')}</MenuItem>
              {levelsUpTo(wikiItemLevelMax).map((l) => <MenuItem key={l} value={l.toString()}>{l}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        {/* Sort - desktop only */}
        {isSm && hasSorting && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('filters.sort')}</InputLabel>
            <Select
              value={sortBy}
              label={t('filters.sort')}
              onChange={(e) => handleSortChange(e.target.value)}
              sx={{ '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Clear button - desktop only */}
        {isSm && hasAnyFilter && (
          <GlassButton
            glassVariant="secondary"
            size="small"
            onClick={clearAll}
            sx={{ minHeight: 0, py: '5px', px: 1.5, fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, flexShrink: 0, whiteSpace: 'nowrap', backdropFilter: 'none', '&:hover': { boxShadow: 'none', borderColor: BORDER_COLORS.FOCUS } }}
          >
            <ClearIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {t('filters.clear')}
          </GlassButton>
        )}
      </Box>

      {/* Mobile Filters Drawer */}
      <Drawer
        anchor="bottom"
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        PaperProps={{
          sx: {
            background: BG_COLORS.CARD,
            backdropFilter: 'blur(10px)',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            maxHeight: '80vh',
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            borderBottom: 'none',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            pb: 1,
            borderBottom: `1px solid ${BORDER_COLORS.DEFAULT}`,
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_COLORS.PRIMARY }}>
              {t('filters.title')}
            </Typography>
            <IconButton onClick={() => setFiltersDrawerOpen(false)} size="small">
              <CloseIcon sx={{ color: TEXT_COLORS.MUTED }} />
            </IconButton>
          </Box>

          {/* Sort */}
          {hasSorting && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: TEXT_COLORS.SECONDARY }}>
                {t('filters.sort')}
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  sx={{ '& .MuiSelect-select': { py: 1 } }}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Filters */}
          {hasRankFilter && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: TEXT_COLORS.SECONDARY }}>
                {t('filters.rank')}
              </Typography>
              <FormControl fullWidth size="small">
                <Select value={rank} onChange={(e) => handleSelectChange('rank', e.target.value)}>
                  <MenuItem value="">{t('filters.allRanks')}</MenuItem>
                  {RANKS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {hasCrystalFilter && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: TEXT_COLORS.SECONDARY }}>
                {t('filters.crystal')}
              </Typography>
              <FormControl fullWidth size="small">
                <Select value={crystalType} onChange={(e) => handleSelectChange('crystalType', e.target.value)}>
                  <MenuItem value="">{t('filters.allCrystals')}</MenuItem>
                  {CRYSTAL_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {hasOperatorTypeFilter && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: TEXT_COLORS.SECONDARY }}>
                {t('filters.operatorType')}
              </Typography>
              <FormControl fullWidth size="small">
                <Select value={operatorType} onChange={(e) => handleSelectChange('operatorType', e.target.value)}>
                  <MenuItem value="">{t('filters.allOperatorTypes')}</MenuItem>
                  {OPERATOR_TYPES.map((op) => <MenuItem key={op} value={op}>{t(`filters.operatorTypes.${op}`)}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {hasLevelFilter && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: TEXT_COLORS.SECONDARY }}>
                {t('filters.level')}
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={level}
                  onChange={(e) => handleSelectChange('level', e.target.value)}
                  disabled={npcLevelsLoading || npcLevels.length === 0}
                  MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                >
                  <MenuItem value="">{t('filters.allLevels')}</MenuItem>
                  {npcLevels.map((l) => <MenuItem key={l} value={l.toString()}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {hasWikiItemLevelFilter && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: TEXT_COLORS.SECONDARY }}>
                {t('filters.level')}
              </Typography>
              <FormControl fullWidth size="small">
                <Select value={wikiItemLevel} onChange={(e) => handleSelectChange('wikiItemLevel', e.target.value)}>
                  <MenuItem value="">{t('filters.allLevels')}</MenuItem>
                  {levelsUpTo(wikiItemLevelMax).map((l) => <MenuItem key={l} value={l.toString()}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <GlassButton
              fullWidth
              glassVariant="secondary"
              onClick={clearAll}
            >
              {t('filters.clear')}
            </GlassButton>
            <GlassButton
              fullWidth
              onClick={() => {
                setFiltersDrawerOpen(false);
                applyAllFilters();
              }}
            >
              {t('filters.apply')}
            </GlassButton>
          </Box>
        </Box>
      </Drawer>
    </>
  );
});

// ─── Виртуализированная сетка карточек (см. docs/wiki.md §3b) ────────────────
// Рисуются только видимые строки (+ запас), остальное — отступы нужной высоты. Строка = мини-сетка
// из `columns` карточек, её высота меряется по факту (карточки в строке тянутся до самой высокой).

type WikiListItem = WikiNewItem | WikiNpcItem | WikiSkillItem;

const getItemKey = (item: WikiListItem): string | number =>
  'skill_id' in item ? `skill-${item.skill_id}` : item.id;

/**
 * Минимальная ширина карточки. ~125px — подписи статов («Физическая Атака:») ломаются в две строки;
 * 164px (1667 с панелью, 4 в ряд) и 176px (1667 без панели, 6 в ряд) юзер счёл тесными. 200: 1667 —
 * 5 / с панелью 3; MacBook 1440 — 4 / с панелью 2; fullHD — 6; телефоны 360–430 — 1.
 */
const MIN_CARD_WIDTH = 200;
const MAX_COLUMNS = 6;
const MAX_COLUMNS_WITH_PANEL = 4;

/** Сколько строк рисовать за краем экрана сверху и снизу. */
const ROW_OVERSCAN = 3;

interface CardRowProps {
  rowItems: WikiListItem[];
  columns: number;
  gapPx: number;
  isLast: boolean;
  renderCard: (item: WikiListItem) => React.ReactNode;
}

// memo: при прокрутке список перерисовывается на каждый кадр, а строки с теми же карточками — нет.
const CardRow = React.memo(({ rowItems, columns, gapPx, isLast, renderCard }: CardRowProps) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    columnGap: gapPx,
    paddingBottom: isLast ? 0 : gapPx,
  }}>
    {rowItems.map(renderCard)}
  </div>
));

interface VirtualCardGridProps {
  items: WikiListItem[];
  columns: number;
  gapPx: number;
  scrollElement: HTMLDivElement | null;
  renderCard: (item: WikiListItem) => React.ReactNode;
  getItemKey: (item: WikiListItem) => string | number;
  /** Наружу — экземпляр виртуализатора: по нему сохраняется и восстанавливается позиция. */
  virtualizerRef: React.MutableRefObject<Virtualizer<HTMLDivElement, Element> | null>;
  /** Высоты строк, померенные до ухода со списка (возврат «назад»). Читаются только при монтировании. */
  initialRowSizes?: [string | number, number][];
}

const VirtualCardGrid: React.FC<VirtualCardGridProps> = ({
  items, columns, gapPx, scrollElement, renderCard, getItemKey, virtualizerRef, initialRowSizes,
}) => {
  // Виртуализатор кладёт ключ+размер из initialMeasurementsCache в свой кэш высот; остальные поля
  // элемента ему для этого не нужны.
  const [initialMeasurementsCache] = useState(
    () => (initialRowSizes ?? []).map(([key, size]) => ({ key, size }) as VirtualItem)
  );
  const rows = React.useMemo(() => {
    const result: WikiListItem[][] = [];
    for (let i = 0; i < items.length; i += columns) result.push(items.slice(i, i + columns));
    return result;
  }, [items, columns]);

  const virtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    count: rows.length,
    getScrollElement: () => scrollElement,
    // Приблизительно; реальная высота меряется measureElement.
    estimateSize: () => 260 + gapPx,
    overscan: ROW_OVERSCAN,
    getItemKey: (index) => getItemKey(rows[index][0]),
    initialMeasurementsCache,
    // Мерить строки в следующем кадре, а не прямо в колбэке ResizeObserver: иначе смена высоты в том же
    // кадре даёт «ResizeObserver loop completed with undelivered notifications» (в dev — красный оверлей).
    useAnimationFrameWithResizeObserver: true,
  });
  virtualizerRef.current = virtualizer as unknown as Virtualizer<HTMLDivElement, Element>;

  const virtualRows = virtualizer.getVirtualItems();

  // Высота контейнера — полная (getTotalSize), а видимые строки сдвинуты внутрь. НЕ отступами сверху/снизу
  // от видимых строк: на первом кадре виртуализатор ещё не знает высоту окна и не отдаёт ни одной строки,
  // контейнер получался нулевым — и восстановление позиции «назад» упиралось в нулевую высоту прокрутки.
  return (
    <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${virtualRows[0]?.start ?? 0}px)`,
      }}>
        {virtualRows.map((virtualRow) => (
          <div key={virtualRow.key} data-index={virtualRow.index} ref={virtualizer.measureElement}>
            <CardRow
              rowItems={rows[virtualRow.index]}
              columns={columns}
              gapPx={gapPx}
              isLast={virtualRow.index === rows.length - 1}
              renderCard={renderCard}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main Grid Component ─────────────────────────────────────────────────────

interface WikiItemGridProps {
  source?: string;
  typeId?: number;
  subtypeId?: number;
  wikiSubCategory?: number;
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
  onTotalPagesChange?: (totalPages: number) => void;
  onPreview?: (itemId: number, detailUrl: string) => void;
  isPreviewOpen?: boolean;
  /** id карточки, открытой в боковой панели (у скилла — skill_id): её подсвечиваем. */
  selectedItemId?: number | null;
}

const WikiItemGrid: React.FC<WikiItemGridProps> = ({
  source,
  typeId,
  subtypeId,
  wikiSubCategory,
  raceId,
  skillTypeId,
  classId,
  professionId,
  onTotalPagesChange,
  onPreview,
  isPreviewOpen = false,
  selectedItemId = null
}) => {
  const location = useLocation();
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  // noSsr: без него на первом рендере все запросы — false, и десктоп сперва грузил порцию телефона (12),
  // а сразу за ней — свою (50). И колонки на первом кадре были бы неверными.
  const isSm = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const isLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const { showError } = useSnackbar();
  const { getSourceChain } = useWikiTypes();

  // Dynamic page size
  const pageSize = isLg
    ? INFINITE_SCROLL_PAGE_SIZE.lg
    : isMd
      ? INFINITE_SCROLL_PAGE_SIZE.md
      : isSm
        ? INFINITE_SCROLL_PAGE_SIZE.sm
        : INFINITE_SCROLL_PAGE_SIZE.xs;
  // Размер порции фиксируется на всё время жизни загруженного списка: новая загрузка (категория, фильтры,
  // язык) берёт текущий, а дозагрузка — тот, с которым список начинали. Иначе смена ширины (поворот
  // планшета через 900px: 20 → 30) перезагружала список с нуля, и прокрутка улетала в начало;
  // а без перезагрузки страница N нового размера легла бы со сдвигом — дубли/дыры.
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const listPageSizeRef = useRef(pageSize);

  // State
  const [items, setItems] = useState<(WikiNewItem | WikiNpcItem | WikiSkillItem)[]>([]);
  const [loading, setLoading] = useState(true);

  // URL params (for initialization & sync)
  const [searchParams, setSearchParams] = useSearchParams();

  // Applied filters (initialized from URL, like admin pattern)
  const [searchFilter, setSearchFilter] = useState(() => searchParams.get('search') || '');
  const [sortByFilter, setSortByFilter] = useState(() => searchParams.get('sortBy') || DEFAULT_SORT);
  const [rankFilter, setRankFilter] = useState(() => searchParams.get('rank') || '');
  const [crystalTypeFilter, setCrystalTypeFilter] = useState(() => searchParams.get('crystalType') || '');
  const [operatorTypeFilter, setOperatorTypeFilter] = useState(() => searchParams.get('operatorType') || '');
  const [levelFilter, setLevelFilter] = useState(() => searchParams.get('level') || '');
  const [wikiItemLevelFilter, setWikiItemLevelFilter] = useState(() => searchParams.get('wikiItemLevel') || '');

  const initialSearchRef = useRef(searchFilter);

  // Clear trigger for child filter bar
  const [clearTrigger, setClearTrigger] = useState(0);

  // NPC levels loaded from API
  const [npcLevels, setNpcLevels] = useState<number[]>([]);
  const [npcLevelsLoading, setNpcLevelsLoading] = useState(false);

  // Navigation key for detecting category changes
  const navigationKey = `${source}-${typeId}-${subtypeId}-${wikiSubCategory}-${raceId}-${skillTypeId}-${classId}-${professionId}`;
  // Стартуем с текущего ключа: первое открытие страницы — не смена категории, фильтры из адреса не трогаем.
  const lastNavigationKeyRef = useRef(navigationKey);

  // Позиция для возврата «назад»: читаем один раз при монтировании, если пришли по истории.
  const navigationType = useNavigationType();
  const restoreRef = useRef<SavedListPosition | null>(
    navigationType === 'POP' ? readSavedPosition(location.key) : null
  );
  const pendingAnchorRef = useRef<{ index: number; offset: number } | null>(null);
  const restoreSeqRef = useRef(0);
  const scrollNodeRef = useRef<HTMLDivElement | null>(null);
  // Узел прокрутки ещё и в state: виртуализатору нужен ре-рендер, когда SimpleBar его смонтировал.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(null);
  const restoredRowSizesRef = useRef<{ sizes: [string | number, number][]; columns: number } | null>(null);

  // Колонки — по РЕАЛЬНОЙ ширине сетки: сколько влезает карточек от MIN_CARD_WIDTH, но не больше 6
  // (с панелью детали — 4). Раньше считались по ширине окна, и на 1440px с открытой панелью выходило
  // 4 колонки по ~125px — подписи статов ломались в две строки. Пока ширина не измерена — прежние
  // брейкпоинты окна. Обратной связи нет: ширину сетке задаёт родитель, а не число колонок.
  const upW450 = useMediaQuery('(min-width: 450px)', { noSsr: true });
  const upW1400 = useMediaQuery('(min-width: 1400px)', { noSsr: true });
  const isXl = useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true });
  const gapPx = parseFloat(theme.spacing(isSm ? SPACING.gridGap.sm : SPACING.gridGap.xs));
  const [gridWidth, setGridWidth] = useState(0);
  useEffect(() => {
    if (!scrollElement) return;
    const measure = () => setGridWidth(scrollElement.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scrollElement);
    return () => ro.disconnect();
  }, [scrollElement]);
  const maxColumns = isPreviewOpen ? MAX_COLUMNS_WITH_PANEL : MAX_COLUMNS;
  const columns = gridWidth > 0
    ? Math.max(1, Math.min(maxColumns, Math.floor((gridWidth + gapPx) / (MIN_CARD_WIDTH + gapPx))))
    : isPreviewOpen
      ? (isXl || upW1400 ? 4 : isLg ? 3 : 2)
      : (isXl ? 6 : upW1400 ? 5 : isLg ? 4 : isSm ? 3 : upW450 ? 2 : 1);
  const itemsCountRef = useRef(0);
  const historyKeyRef = useRef(location.key);
  historyKeyRef.current = location.key;
  const navigationKeyRef = useRef(navigationKey);
  navigationKeyRef.current = navigationKey;

  // Get source config for filters
  const sourceChain = React.useMemo(() => {
    return getSourceChain({
      apiSource: source || '',
      subcategoryId: wikiSubCategory,
      typeId,
      subtypeId,
      raceId,
      skillTypeId,
      classId,
      professionId
    });
  }, [getSourceChain, source, wikiSubCategory, typeId, subtypeId, raceId, skillTypeId, classId, professionId]);

  const sourceConfig: SourceConfig | null = React.useMemo(() => {
    return getSourceConfig(sourceChain);
  }, [sourceChain]);

  // Check which filters are available
  const hasLevelFilter = sourceConfig?.filters?.some(f => f.type === 'level') || false;
  const hasRankFilter = sourceConfig?.filters?.some(f => f.type === 'rank') || false;
  const hasCrystalFilter = sourceConfig?.filters?.some(f => f.type === 'crystal') || false;
  const hasOperatorTypeFilter = sourceConfig?.filters?.some(f => f.type === 'operatorType') || false;
  const hasWikiItemLevelFilter = sourceConfig?.filters?.some(f => f.type === 'wikiItemLevel') || false;
  // Верхняя граница диапазона «Уровень» — из конфига source (напр. 18 для precious stones/runes), дефолт 10.
  const wikiItemLevelMax = sourceConfig?.filters?.find(f => f.type === 'wikiItemLevel')?.max ?? 10;
  const hasSorting = !!(sourceConfig?.sorting && sourceConfig.sorting.length > 0);
  const hasAnyFilter = hasLevelFilter || hasRankFilter || hasCrystalFilter || hasOperatorTypeFilter || hasWikiItemLevelFilter;

  // Load NPC levels from API when subcategory changes
  useEffect(() => {
    if (hasLevelFilter && wikiSubCategory) {
      setNpcLevelsLoading(true);
      wikiNewApi.getNpcLevels(wikiSubCategory)
        .then(levels => {
          setNpcLevels(levels);
        })
        .catch(() => {
          setNpcLevels([]);
        })
        .finally(() => {
          setNpcLevelsLoading(false);
        });
    } else {
      setNpcLevels([]);
    }
  }, [hasLevelFilter, wikiSubCategory]);

  // Sort options
  const sortOptions = React.useMemo(() => {
    if (!sourceConfig?.sorting || sourceConfig.sorting.length === 0) {
      return [{ value: 'name', label: t('filters.sortName') + ' ↑' }, { value: 'name_desc', label: t('filters.sortName') + ' ↓' }];
    }
    const options: { value: string; label: string }[] = [];
    sourceConfig.sorting.forEach(sort => {
      options.push({ value: sort.field, label: t(sort.labelKey) + ' ↑' });
      options.push({ value: `${sort.field}_desc`, label: t(sort.labelKey) + ' ↓' });
    });
    return options;
  }, [sourceConfig, t]);

  // Build filters object from applied filter states
  const buildFilters = useCallback((): Partial<WikiNewItemFilters> => {
    const filters: any = { sortBy: sortByFilter };
    if (searchFilter) filters.search = searchFilter;
    if (rankFilter) filters.rank = rankFilter;
    if (crystalTypeFilter) filters.crystalType = crystalTypeFilter;
    if (operatorTypeFilter) filters.operatorType = operatorTypeFilter;
    if (levelFilter) filters.level = parseInt(levelFilter);
    if (wikiItemLevelFilter) filters.wikiItemLevel = parseInt(wikiItemLevelFilter);
    return filters;
  }, [sortByFilter, searchFilter, rankFilter, crystalTypeFilter, operatorTypeFilter, levelFilter, wikiItemLevelFilter]);

  // Load items ref for infinite scroll
  const loadItemsRef = useRef<((pageNum: number, append: boolean) => Promise<void>) | undefined>(undefined);

  const {
    scrollRef,
    loadingMore,
    page,
    setLoadingMore,
    setHasMore,
    setPage,
    reset: resetScroll,
  } = useInfiniteScroll(
    () => {
      if (loadItemsRef.current) {
        return loadItemsRef.current(page + 1, true);
      }
      return Promise.resolve();
    },
    { threshold: 100 }
  );

  // Load items function
  const loadItems = useCallback(async (
    pageNum: number = 0,
    append: boolean = false
  ) => {
    if (!source) {
      setItems([]);
      setLoading(false);
      return;
    }

    // Don't load if on detail page
    if (isWikiDetailPage(location.pathname)) {
      setLoading(false);
      return;
    }

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setItems([]);
        listPageSizeRef.current = pageSizeRef.current; // новый список — размер порции по текущей ширине
      }

      const filters = buildFilters();
      const apiFilters: WikiNewItemFilters = {
        typeId,
        subtypeId,
        wikiSubCategory,
        raceId,
        skillTypeId,
        classId,
        professionId,
        page: pageNum,
        size: listPageSizeRef.current,
        ...filters
      };

      const response = await wikiNewApi.getItemsBySource(source, apiFilters);

      // Backend can return null entries in content (e.g. a referenced skill that no longer resolves) —
      // drop them so the grid doesn't crash in getItemKey/renderCard on a null item.
      const content = (response.content || []).filter(Boolean);
      if (append) {
        setItems(prev => [...prev, ...content]);
      } else {
        setItems(content);
      }

      setPage(response.page);
      setHasMore(response.page < response.totalPages - 1);
      onTotalPagesChange?.(response.totalPages);

    } catch (err: unknown) {
      showError(getErrorMessage(err, t('messages.loadItemsError')));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [source, typeId, subtypeId, wikiSubCategory, raceId, skillTypeId, classId, professionId, buildFilters, location.pathname, t, showError, setLoadingMore, setHasMore, setPage, onTotalPagesChange]);

  // Update ref
  useEffect(() => {
    loadItemsRef.current = loadItems;
  }, [loadItems]);

  // Возврат по истории: ОДНИМ запросом взять столько, сколько было подгружено, округлив вверх до целых
  // порций (size = pages × размер порции, page 0). Это ровно страницы 0..pages-1 обычной подгрузки, поэтому
  // дальше бесконечный скролл продолжает с page = pages обычным размером — без дублей.
  const restoreItems = useCallback(async (saved: SavedListPosition) => {
    if (!source) return;
    // Эффект загрузки может перезапуститься, пока идёт восстановление (StrictMode в dev, быстрая смена
    // фильтра). Применяем только последний запуск, иначе в список ляжет устаревший ответ.
    const seq = ++restoreSeqRef.current;
    const size = pageSizeRef.current;
    listPageSizeRef.current = size;
    const pages = Math.max(1, Math.ceil(saved.count / size));
    try {
      setLoading(true);
      setItems([]);
      const response = await wikiNewApi.getItemsBySource(source, {
        typeId, subtypeId, wikiSubCategory, raceId, skillTypeId, classId, professionId,
        page: 0,
        size: pages * size,
        ...buildFilters(),
      });
      if (seq !== restoreSeqRef.current) return;
      // Сбрасываем только после успеха: в StrictMode эффект загрузки отрабатывает дважды,
      // и второй прогон тоже должен пойти по пути восстановления, а не грузить с нуля.
      restoreRef.current = null;
      pendingAnchorRef.current = { index: saved.anchorIndex, offset: saved.anchorOffset };
      restoredRowSizesRef.current = saved.rowSizes && saved.columns
        ? { sizes: saved.rowSizes, columns: saved.columns }
        : null;
      // totalPages в ответе посчитан под большой size — пересчитываем в обычных порциях.
      const totalPages = Math.ceil(response.totalElements / size);
      setItems((response.content || []).filter(Boolean));
      setPage(pages - 1);
      setHasMore(pages < totalPages);
      onTotalPagesChange?.(totalPages);
    } catch (err: unknown) {
      if (seq !== restoreSeqRef.current) return;
      restoreRef.current = null;
      showError(getErrorMessage(err, t('messages.loadItemsError')));
    } finally {
      if (seq === restoreSeqRef.current) setLoading(false);
    }
  }, [source, typeId, subtypeId, wikiSubCategory, raceId, skillTypeId, classId, professionId, buildFilters, t, showError, setHasMore, setPage, onTotalPagesChange]);

  // Прокрутку ставим, когда восстановленные карточки уже отданы виртуализатору (его layout-эффект
  // отрабатывает раньше нашего — он дочерний). Начало строки-якоря считается по сохранённым реальным
  // высотам строк над ней (rowSizes), а где их нет — по оценке.
  useLayoutEffect(() => {
    const node = scrollNodeRef.current;
    const virtualizer = virtualizerRef.current;
    const anchor = pendingAnchorRef.current;
    if (!anchor || !node || !virtualizer || loading || items.length === 0) return;
    const row = Math.min(Math.floor(anchor.index / columns), Math.ceil(items.length / columns) - 1);
    const rowStart = virtualizer.getOffsetForIndex(row, 'start')?.[0] ?? 0;
    // Прямо в scrollTop, НЕ virtualizer.scrollToOffset: тот несколько кадров удерживает заданные пиксели и
    // глушит собственную поправку, когда строки над якорем меряются по факту (якорь съезжал на ~40px).
    node.scrollTop = rowStart + anchor.offset;
    pendingAnchorRef.current = null;
  }, [items, loading, columns]);

  itemsCountRef.current = items.length;
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  // ─── Смена числа колонок (открыли/закрыли панель детали, повернули экран) ───
  // Прокрутка в пикселях при этом остаётся, а строки перекладываются — содержимое уезжает. Держим на месте
  // карточку, что была вверху экрана; а если колонки сменились из-за открытия панели — показываем
  // карточку, которую открыли (если она ушла за экран). scrollToIndex, а не пиксели: он доводит строку
  // до места и после того, как строки над ней померятся по факту.
  const topItemIndexRef = useRef(0);
  const previewedIdRef = useRef<number | null>(null);
  const prevColumnsRef = useRef(columns);
  const prevPreviewOpenRef = useRef(isPreviewOpen);

  useEffect(() => {
    if (!scrollElement) return;
    const onScroll = () => {
      const row = virtualizerRef.current?.getVirtualItemForOffset(scrollElement.scrollTop);
      topItemIndexRef.current = row ? row.index * columnsRef.current : 0;
    };
    scrollElement.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', onScroll);
  }, [scrollElement]);

  useLayoutEffect(() => {
    const prevColumns = prevColumnsRef.current;
    const panelJustOpened = isPreviewOpen && !prevPreviewOpenRef.current;
    prevColumnsRef.current = columns;
    prevPreviewOpenRef.current = isPreviewOpen;
    const virtualizer = virtualizerRef.current;
    if (prevColumns === columns || !virtualizer || items.length === 0) return;

    const previewedIndex = panelJustOpened && previewedIdRef.current !== null
      ? items.findIndex(item => ('skill_id' in item ? item.skill_id : item.id) === previewedIdRef.current)
      : -1;
    if (previewedIndex >= 0) {
      virtualizer.scrollToIndex(Math.floor(previewedIndex / columns), { align: 'auto' });
    } else {
      virtualizer.scrollToIndex(Math.floor(topItemIndexRef.current / columns), { align: 'start' });
    }
  }, [columns, isPreviewOpen, items]);

  // Запоминаем, какую карточку открыли в панели, — для прокрутки выше.
  const handlePreview = useCallback((itemId: number, detailUrl: string) => {
    previewedIdRef.current = itemId;
    onPreview?.(itemId, detailUrl);
  }, [onPreview]);
  const cardPreview = onPreview ? handlePreview : undefined;

  // Уходя со списка (открыли карточку) — запомнить позицию под ключом текущей записи истории.
  // Именно layout-эффект: его очистка идёт, пока узел ещё в DOM, и scrollTop читается честно.
  useLayoutEffect(() => () => {
    const scrollTop = scrollNodeRef.current?.scrollTop ?? 0;
    const virtualizer = virtualizerRef.current;
    const topRow = virtualizer?.getVirtualItemForOffset(scrollTop);
    writeSavedPosition(historyKeyRef.current, {
      navigationKey: navigationKeyRef.current,
      count: itemsCountRef.current,
      anchorIndex: topRow ? topRow.index * columnsRef.current : 0,
      anchorOffset: topRow ? scrollTop - topRow.start : 0,
      // Ключи строк — наши (getItemKey: id или 'skill-…'), bigint там не бывает.
      rowSizes: virtualizer
        ? Array.from(virtualizer.itemSizeCache.entries()) as [string | number, number][]
        : undefined,
      columns: columnsRef.current,
      loadId: PAGE_LOAD_ID,
    });
  }, []);

  const setScrollNode = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      scrollNodeRef.current = node;
      setScrollElement(node);
    }
    scrollRef(node);
  }, [scrollRef]);

  // Load items when filters or navigation changes
  useEffect(() => {
    const saved = restoreRef.current;
    if (saved && saved.navigationKey === navigationKey && saved.count > 0) {
      resetScroll();
      restoreItems(saved);
      return;
    }
    restoreRef.current = null;
    restoreSeqRef.current++; // незавершённое восстановление больше не применять

    // Clear filters when category changes
    if (navigationKey !== lastNavigationKeyRef.current) {
      lastNavigationKeyRef.current = navigationKey;
      setSearchFilter('');
      setSortByFilter(DEFAULT_SORT);
      setRankFilter('');
      setCrystalTypeFilter('');
      setOperatorTypeFilter('');
      setLevelFilter('');
      setWikiItemLevelFilter('');
      setSearchParams(new URLSearchParams(), { replace: true });
      setClearTrigger(prev => prev + 1);
    }
    resetScroll();
    loadItems(0, false);
  }, [navigationKey, searchFilter, sortByFilter, rankFilter, crystalTypeFilter, operatorTypeFilter, levelFilter, wikiItemLevelFilter, loadItems, restoreItems, resetScroll, setSearchParams]);

  // Reload on language change
  useLocaleChange(useCallback(() => {
    resetScroll();
    loadItems(0, false);
  }, [loadItems, resetScroll]));

  // Handle filters change from child (like admin handleFiltersChange)
  const handleFiltersChange = useCallback((filters: WikiAppliedFilters) => {
    setSearchFilter(filters.search);
    setSortByFilter(filters.sortBy);
    setRankFilter(filters.rank);
    setCrystalTypeFilter(filters.crystalType);
    setOperatorTypeFilter(filters.operatorType);
    setLevelFilter(filters.level);
    setWikiItemLevelFilter(filters.wikiItemLevel);

    // Sync to URL
    const params: Record<string, string> = {};
    if (filters.search) params.search = filters.search;
    if (filters.sortBy && filters.sortBy !== DEFAULT_SORT) params.sortBy = filters.sortBy;
    if (filters.rank) params.rank = filters.rank;
    if (filters.crystalType) params.crystalType = filters.crystalType;
    if (filters.operatorType) params.operatorType = filters.operatorType;
    if (filters.level) params.level = filters.level;
    if (filters.wikiItemLevel) params.wikiItemLevel = filters.wikiItemLevel;
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Card renderer — useCallback: строки сетки (CardRow) мемоизированы и перерисуются только при смене функции
  const renderCard = useCallback((item: WikiListItem) => {
    if (!source || !item) return null;
    const key = getItemKey(item);
    const selected = ('skill_id' in item ? item.skill_id : item.id) === selectedItemId;

    if (source === 'npcs') {
      return <WikiNpcCard key={key} npc={item as WikiNpcItem} source={source} wikiSubCategory={wikiSubCategory} onPreview={cardPreview} selected={selected} />;
    } else if (source === 'skills') {
      return <WikiSkillCard key={key} skill={item as WikiSkillItem} source={source} raceId={raceId} skillTypeId={skillTypeId} classId={classId} professionId={professionId} onPreview={cardPreview} selected={selected} />;
    } else {
      return <WikiItemCard key={key} item={item as WikiNewItem} source={source} sourceChain={sourceChain} typeId={typeId} subtypeId={subtypeId} wikiSubCategory={wikiSubCategory} raceId={raceId} skillTypeId={skillTypeId} classId={classId} professionId={professionId} onPreview={cardPreview} selected={selected} />;
    }
  }, [source, sourceChain, typeId, subtypeId, wikiSubCategory, raceId, skillTypeId, classId, professionId, cardPreview, selectedItemId]);

  if (!source) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          {t('messages.selectCategory')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Fixed header with search/filters */}
      <Box sx={{ flexShrink: 0, mb: { xs: 1, sm: 2 } }}>
        <WikiFilterBar
          isSm={isSm}
          sortBy={sortByFilter}
          rank={rankFilter}
          crystalType={crystalTypeFilter}
          operatorType={operatorTypeFilter}
          level={levelFilter}
          wikiItemLevel={wikiItemLevelFilter}
          initialSearch={initialSearchRef.current}
          clearTrigger={clearTrigger}
          hasRankFilter={hasRankFilter}
          hasCrystalFilter={hasCrystalFilter}
          hasOperatorTypeFilter={hasOperatorTypeFilter}
          hasLevelFilter={hasLevelFilter}
          hasWikiItemLevelFilter={hasWikiItemLevelFilter}
          wikiItemLevelMax={wikiItemLevelMax}
          hasSorting={hasSorting}
          hasAnyFilter={hasAnyFilter}
          sortOptions={sortOptions}
          npcLevels={npcLevels}
          npcLevelsLoading={npcLevelsLoading}
          onFiltersChange={handleFiltersChange}
        />
      </Box>

      {/* Scrollable content */}
      <SimpleBar
        scrollableNodeProps={{ ref: setScrollNode }}
        style={{ flex: 1, minHeight: 0, paddingTop: 8, paddingRight: isSm ? SCROLL_RESERVE.sm : SCROLL_RESERVE.xs }}
        autoHide={false}
      >
        {loading && page === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 4, sm: 8 } }}>
            <CircularProgress sx={{ color: '#94a3b8' }} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
            <Typography variant="h6" color="text.secondary">
              {t('messages.noResults')}
            </Typography>
          </Box>
        ) : (
          <>
            <VirtualCardGrid
              items={items}
              columns={columns}
              gapPx={gapPx}
              scrollElement={scrollElement}
              renderCard={renderCard}
              getItemKey={getItemKey}
              virtualizerRef={virtualizerRef}
              initialRowSizes={restoredRowSizesRef.current?.columns === columns ? restoredRowSizesRef.current.sizes : undefined}
            />

            {/* Loading more indicator */}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} sx={{ color: 'rgba(139, 92, 246, 0.7)' }} />
              </Box>
            )}
          </>
        )}
      </SimpleBar>
    </Box>
  );
};

export default WikiItemGrid;
